import moment from "moment";
import {
  ConsultantContractType,
  ContractType,
  useUploadSignedConsultantContractMutation,
  useUploadSignedConsultantBookingContractMutation
} from "@bonliva-crm/graphql";
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select
} from "@material-ui/core";
import { Modal, FileUpload, Color, TextField } from "@bonliva-crm/ui";
import { DatePickerController } from "@bonliva-crm/ui/form-controller";
import locale from "@bonliva-crm/locale";
import { makeStyles } from "@material-ui/styles";
import { useSnackbars, useTenant } from "@hooks";
import { ContractUnion } from "../types";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  UploadSignedDocumentFormValues,
  uploadSignedDocumentFormSchema
} from "./form-values";
import { useEffect, useState } from "react";
import { FileUploadConfig, getConsultantAgeGroup } from "@bonliva-crm/shared";
import { DevTool } from "@hookform/devtools";

export const UploadSignedDocumentModal = (props: {
  open: boolean;
  setOpen: (o: boolean) => void;
  action: string;
  contract: ContractUnion;
  customerContract?: boolean;
}) => {
  const classes = useStyles();
  const { showError, showSuccess } = useSnackbars();
  const { tenant } = useTenant(); // Replace with actual tenant hook if available
  const [uploadConsultantContractMutation] = useUploadSignedConsultantContractMutation();
  const [uploadConsultantBookingContractMutation] =
    useUploadSignedConsultantBookingContractMutation();
  const [uploadConfig] = useState(new FileUploadConfig("consultantContract"));

  const { control, watch, handleSubmit, formState, reset, resetField, setValue } =
    useForm<UploadSignedDocumentFormValues>({
      defaultValues: {
        contract: props.contract
      },
      resolver: yupResolver(uploadSignedDocumentFormSchema)
    });

  useEffect(() => {
    reset();
  }, [reset, props.contract]);

  const uploadConsultantContract = ({
    file,
    bankCounselling,
    date,
    contractType,
    expectedSalary,
    payoutType
  }: UploadSignedDocumentFormValues) => {
    uploadConsultantContractMutation({
      variables: {
        contractId: props.contract._id,
        bankCounselling: bankCounselling,
        file: file,
        payoutType: payoutType,
        SalaryChoice: contractType,
        SignedOn: date,
        expectedSalary: expectedSalary
      },
      onCompleted: () => {
        showSuccess(locale.FileUploadSucceeded);
      },
      onError: (error) => {
        showError(locale.cannotPerformAction);
        console.error(error);
      }
    });
  };

  const uploadConsultantBookingContract = ({
    file,
    date
  }: UploadSignedDocumentFormValues) => {
    uploadConsultantBookingContractMutation({
      variables: {
        contractId: props.contract._id,
        file: file,
        SignedOn: date
      },
      onCompleted: () => {
        showSuccess(locale.FileUploadSucceeded);
      },
      onError: (error) => {
        showError(locale.cannotPerformAction);
        console.error(error);
      }
    });
  };

  const renderInputs = () => {
    const datePicker = (label: string) => {
      return (
        <div>
          <DatePickerController
            name="date"
            control={control}
            DatePickerProps={{
              required: true,
              label: label
            }}
          />
        </div>
      );
    };

    const salaryChoice = (showNationalProcurement?: boolean, isAbobe60?: boolean) => {
      let _opt = Object.keys(locale.contractType);
      if (!showNationalProcurement) {
        _opt = _opt.filter((c) => c !== ContractType.NationalProcurement);
      }
      if (tenant === "wcc" && isAbobe60) {
        _opt = _opt.filter((c) => c !== ContractType.HourlyPensionInsurance);
      }
      return (
        <Controller
          render={({ field }) => (
            <>
              <FormLabel required id="label" className={classes.bodyColor}>
                {locale.SalaryChoice}
              </FormLabel>
              <Select {...field} color="secondary">
                {_opt.map((key) => (
                  <MenuItem value={key}>{locale.contractType[key]}</MenuItem>
                ))}
              </Select>
            </>
          )}
          name="contractType"
          control={control}
        />
      );
    };

    const payoutType = () => {
      return (
        <Controller
          render={({ field }) => (
            <FormControl component="fieldset">
              <FormLabel component="legend" className={classes.bodyColor} required>
                {locale.Payout}
              </FormLabel>
              <RadioGroup {...field}>
                {Object.keys(locale.consultantContractPayoutType).map((key) => (
                  <FormControlLabel
                    value={key}
                    control={<Radio />}
                    label={locale.consultantContractPayoutType[key]}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          )}
          name="payoutType"
          control={control}
        />
      );
    };

    const bankCounselling = () => {
      return (
        <Controller
          render={({ field }) => (
            <FormControl
              component="fieldset"
              disabled={watch("contractType") !== ContractType.Hourly}
            >
              <FormLabel
                component="legend"
                className={classes.bodyColor}
                required={watch("contractType") === ContractType.Hourly}
              >
                {locale.BankCounselling}
              </FormLabel>
              <RadioGroup {...field}>
                <FormControlLabel
                  value={"true"}
                  control={<Radio />}
                  label={locale.BankCounsellingOptions.yes}
                />
                <FormControlLabel
                  value={"false"}
                  control={<Radio />}
                  label={locale.BankCounsellingOptions.no}
                />
              </RadioGroup>
            </FormControl>
          )}
          name="bankCounselling"
          control={control}
        />
      );
    };

    const expectedSalary = () => {
      return (
        <Controller
          render={({ field }) => (
            <FormControl
              disabled={watch("contractType") !== ContractType.HourlyPensionInsurance}
            >
              <FormLabel
                id="label"
                className={classes.bodyColor}
                required={watch("contractType") === ContractType.HourlyPensionInsurance}
              >
                {locale.ExpectedSalary}
              </FormLabel>
              <TextField type="number" {...field} />
            </FormControl>
          )}
          name="expectedSalary"
          control={control}
        />
      );
    };

    if (props.contract.Type === ConsultantContractType.Intermittent) {
      if (props.contract.NotRelevantMonthlyWeeklyPayouts) {
        return (
          <>
            {datePicker(locale.DateIntermittentAgreementIsSigned)}
            {salaryChoice()}
            {watch("contractType") === ContractType.Hourly && bankCounselling()}
            {watch("contractType") === ContractType.HourlyPensionInsurance &&
              expectedSalary()}
          </>
        );
      } else {
        return (
          <>
            {datePicker(locale.DateIntermittentAgreementIsSigned)}
            {salaryChoice()}
            {payoutType()}
            {watch("contractType") === ContractType.Hourly && bankCounselling()}
            {watch("contractType") === ContractType.HourlyPensionInsurance &&
              expectedSalary()}
          </>
        );
      }
    } else if (props.contract.Type === ConsultantContractType.Employment) {
      if (props.contract.NotRelevantMonthlyWeeklyPayouts) {
        return (
          <>
            {salaryChoice()}
            {watch("contractType") === ContractType.Hourly && bankCounselling()}
            {watch("contractType") === ContractType.HourlyPensionInsurance &&
              expectedSalary()}
          </>
        );
      } else {
        return (
          <>
            {salaryChoice()}
            {payoutType()}
            {watch("contractType") === ContractType.Hourly && bankCounselling()}
            {watch("contractType") === ContractType.HourlyPensionInsurance &&
              expectedSalary()}
          </>
        );
      }
    } else if (
      props.contract.Type === ConsultantContractType.Framework ||
      props.contract.Type === ConsultantContractType.Framework_National
    ) {
      return <>{datePicker(locale.DateFrameworkAgreementIsSigned)}</>;
    } else if (
      props.contract.Type === ConsultantContractType.Intermittent_National ||
      props.contract.Type === ConsultantContractType.Employment_National
    ) {
      const { below25, above64, above60 } = getConsultantAgeGroup(
        moment(props.contract.Consultant.BirthDate).format("YYYY-MM-DD"),
        moment(props.contract.StartDate).format("YYYY-MM-DD"),
        moment(props.contract.EndDate).format("YYYY-MM-DD")
      );

      if (below25 || above64) {
        return (
          <>
            {props.contract.Type === ConsultantContractType.Intermittent_National &&
              datePicker(locale.DateIntermittentNationalAgreementIsSigned)}
            {salaryChoice(false, above60)}
            {payoutType()}
            {bankCounselling()}
          </>
        );
      } else {
        setValue("contractType", ContractType.NationalProcurement);
        return (
          <>
            {props.contract.Type === ConsultantContractType.Intermittent_National &&
              datePicker(locale.DateIntermittentNationalAgreementIsSigned)}
            {payoutType()}
          </>
        );
      }
    } else if (
      props.contract.Type === ConsultantContractType.BC_Framework ||
      props.contract.Type === ConsultantContractType.BC_Intermittent
    ) {
      return <>{datePicker(locale.DateFrameworkAgreementIsSigned)}</>;
    }

    return null;
  };

  return (
    <Modal
      saveButtonDisabled={!formState.isValid}
      onSubmit={() =>
        handleSubmit(
          props.customerContract
            ? uploadConsultantBookingContract
            : uploadConsultantContract
        )()
      }
      fullHeight
      label={locale.UploadSignedDocument}
      modalContent={
        <>
          <Controller
            render={({ field }) => (
              <FileUpload
                config={uploadConfig}
                onFileUpload={(f) => field.onChange(f)}
                onFileRemove={() => resetField("file")}
              >
                {renderInputs()}
              </FileUpload>
            )}
            name="file"
            control={control}
          />
          <DevTool control={control} />
        </>
      }
      open={props.open}
      setOpen={props.setOpen}
      dimensions={{
        minHeight: "fit-content",
        minWidth: "50%"
      }}
    />
  );
};

const useStyles = makeStyles(() => ({
  bodyColor: { color: Color.Body }
}));
